<template>
  <div>
    <div>
      <el-input
        ref="BLMCheckTextInputEditor"
        v-loading="loading"
        :value="value"
        @input="onInput"
        @change="onChange"
        v-on="$listeners"
        @blur="onBlur"
        @focus="onFocus"
        :clearable="clearable"
        :readonly="readonly"
        :show-word-limit="showWordLimit"
        :maxlength="maxlength"
        :placeholder="placeholder"
        :disabled="disabled"
        :rows="rows"
        :type="type"
      />
    </div>
  </div>
  </template>

<script>
import { checkTextSafe, checkTextSafeHubble } from './api.js'

export default {
  name: 'QSCheckTextInput',
  props: {
    value: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    rows: {
      type: Number,
      default: 2,
    },
    showWordLimit: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: [Number, String],
      default: undefined,
    },
    request: {
      type: Function,
      default: () => null,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      formItemProp: '',
    }
  },
  computed: {
    // eslint-disable-next-line vue/no-unused-properties
    isFirefox() {
      return navigator.userAgent.includes('Firefox')
    },
    isHubble() {
      const isHubble = /\bhubble\b/gi.test(location.host)
      const hubbleTest = /\b3333\b/.test(location.host)
      return isHubble || hubbleTest
    },
  },
  created() {
    this.checkResLabelCategories = {
      normal: '正常文本',
      spam: '含垃圾信息',
      ad: '广告',
      politics: '政治',
      terrorism: '暴恐',
      abuse: '辱骂',
      porn: '色情',
      flood: '灌水',
      contraband: '违禁',
      meaningless: '无意义',
      customized: '自定义（比如命中自定义关键词）',
    }
  },
  mounted() {
    this.setFormItemProp()
    this.initFormRules()
  },
  beforeDestroy() {
    this.removeFormRules()
  },
  methods: {
    // 火狐
    onBlur() {
      this.$emit('blur')
      this.initFormRules()
    },
    onInput() {
      this.$emit('input', this.value)
    },
    onFocus() {
      this.$emit('focus')
    },
    onChange() {
      this.$emit('change')
    },
    setFormItemProp() {
      const parent = this.deepParent(this.$parent)
      this.formItemProp = parent.prop
    },
    /**
     * 给业务组件提供的插入文本的方法
     */
    // eslint-disable-next-line vue/no-unused-properties
    insertText(leftValue = '', rightValue = '') {
      // 获取input 光标点
      const rangeIndex = this.$refs?.BLMCheckTextInputEditor?.$refs[this.type === 'text' ? 'input' : 'textarea']?.selectionStart || 0
      if (this.value) {
        this.$emit('input', this.value.slice(0, rangeIndex) + leftValue + rightValue + this.value.slice(rangeIndex))
      } else {
        this.$emit('input', leftValue + rightValue)
      }
      this.$refs?.BLMCheckTextInputEditor?.$refs[this.type === 'text' ? 'input' : 'textarea']?.focus()
      this.$nextTick(() => {
        const index = rangeIndex + leftValue?.length
        this.$refs?.BLMCheckTextInputEditor?.$refs[this.type === 'text' ? 'input' : 'textarea']?.setSelectionRange(index, index)
      })
    },
    /**
       * 处理给父级Form添加rules
       */
    initFormRules() {
      const parent = this.deepParent(this.$parent)
      if (
        parent.form.rules[parent.prop] &&
          parent.form.rules[parent.prop].some(
            (rule) => rule.name === 'QSCheckTextInput',
          )
      ) {
        return
      }
      parent.form.rules[parent.prop].push({
        validator: async (rule, value, callback) => {
          try {
            if (value) {
              callback(await this.checkWords(value))
            } else {
              callback()
            }
          } catch (error) {
            callback('服务或网络错误，请重试')
          }
        },
        name: 'QSCheckTextInput',
        trigger: 'blur',
      })
    },
    removeFormRules() {
      const parent = this.deepParent(this.$parent)
      if (parent.form.rules && parent.form.rules[this.formItemProp]) {
        const index = parent.form.rules[this.formItemProp].findIndex(
          (rule) => rule.name === 'QSCheckTextInput',
        )
        index > -1 && parent.form.rules[this.formItemProp].splice(index, 1)
      }
    },
    /**
       * 火狐校验文字是否违规
       */
    async checkWords(value) {
      try {
        this.loading = true
        const privateCheckTextSafe = this.isHubble
          ? checkTextSafeHubble
          : checkTextSafe
        const checkRes = await privateCheckTextSafe(this.request, {
          content: value,
        })
        this.loading = false
        /**
           * 异步check过程中可能会终止check
           */
        if (
          checkRes.data &&
            checkRes.data.results[0] &&
            checkRes.data.results[0].suggestion === 'block'
        ) {
          return `配置信息涉嫌“${
            this.checkResLabelCategories[checkRes.data.results[0].label]
          }”，请重新配置`
        }
        /**
           * 文本内容安全事件检测通过时触发
           */
        this.$nextTick(() => {
          this.$emit('validatePassed')
        })
      } catch (error) {
        this.loading = false
        console.log('error: ', error)
      }
    },
    /**
       * 向上查找form父级(控制表单状态)
       */
    deepParent(parent) {
      if (parent) {
        if (parent.form) {
          return parent
        } else if (parent.$parent) {
          return this.deepParent(parent.$parent)
        }
      }
    },
  },
}
</script>

  <style lang="scss" scoped>
  .check-text-input {
    position: relative;
    overflow: hidden;
    cursor: text;
    .editor-input-wrapper {
      position: absolute;
      z-index: 10;
      padding: 0 12px;
      display: flex;
      width: 100%;
      .editor {
        max-width: 100%;
        white-space: nowrap;
        overflow-x: auto;
        flex: 1;
      }
      .editor:empty:before {
        content: attr(placeholder);
        color: #bcbdc0;
        pointer-events: none;
      }
      .editor:focus:before {
        content: none;
      }
      .editor-action {
        font-size: 12px;
        .icon-action-close {
          color: #bbbdc1;
          cursor: pointer;
        }
        .icon-action-close:hover {
          color: #848587;
        }
      }
      .icon-action {
        margin-left: 2px;
      }
      .editor-input-count {
        margin-left: 5px;
        color: #323233;
        font-size: 12px;
        user-select: none;
        pointer-events: none;
      }
    }
    .editor-textarea-wrapper {
      position: absolute;
      z-index: 10;
      padding: 6px 12px;
      width: 100%;
      height: 100%;
      .editor {
        line-height: 1.5;
        max-width: 100%;
        height: 100%;
        overflow-y: auto;
        word-wrap: break-word;
        word-break: break-all;
      }
      .editor:empty:before {
        content: attr(placeholder);
        color: #bcbdc0;
        pointer-events: none;
      }
      .editor:focus:before {
        content: none;
      }
      .editor-textarea-count {
        color: #323233;
        background: #fff;
        font-size: 12px;
        position: absolute;
        bottom: 5px;
        right: 10px;
        user-select: none;
        pointer-events: none;
      }
    }
    .editor-wrapper-disabled {
      color: #c8c9cc;
    }
  }
  </style>

